import { supabase } from './supabase';

export interface CoachSuccessResponse {
  ok: true;
  answer: string;
  conversation_id: string;
  diploma: string;
  subject_code: string;
  subject_label: string;
}

export interface CoachPaywallResponse {
  ok: false;
  type: 'PAYWALL';
  code: string;
  feature: string;
  message: string;
}

// ✅ NOUVEAU: Type pour la limite journalière Premium
export interface CoachRateLimitResponse {
  ok: false;
  type: 'RATE_LIMIT';
  code: string;
  feature: string;
  message: string;
}

export interface CoachErrorResponse {
  ok: false;
  type: 'ERROR';
  error: string;
}

export type CoachResponse = CoachSuccessResponse | CoachPaywallResponse | CoachRateLimitResponse | CoachErrorResponse;

// ✅ AJOUT: Type pour les images
export interface CoachImage {
  base64: string;
  mime_type: string;
  file_name: string;
}

interface SendCoachMessageParams {
  message: string;
  conversationId?: string | null;
  diploma?: string;
  subjectCode?: string;
  subjectLabel?: string;
  documentId?: string | null;
  images?: CoachImage[];  // ✅ AJOUT
}

export async function sendCoachMessage(params: SendCoachMessageParams): Promise<CoachResponse> {
  const { message, conversationId, diploma, subjectCode, subjectLabel, documentId, images } = params;

  const trimmed = message.trim();
  if (!trimmed) {
    return { ok: false, type: 'ERROR', error: 'Message vide' };
  }

  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !sessionData.session) {
    return { ok: false, type: 'ERROR', error: 'Utilisateur non authentifié' };
  }

  const body: Record<string, unknown> = {
    message: trimmed,
  };

  if (conversationId) {
    body.conversation_id = conversationId;
  }
  if (diploma) {
    body.diploma = diploma;
  }
  if (subjectCode) {
    body.subject_code = subjectCode;
  }
  if (subjectLabel) {
    body.subject_label = subjectLabel;
  }
  if (documentId) {
    body.document_id = documentId;
  }
  // ✅ AJOUT: Passer les images à l'edge function
  if (images && images.length > 0) {
    body.images = images;
  }

  const { data, error } = await supabase.functions.invoke('coach-chat', {
    body,
  });

  // PAYWALL response (Free limit)
  if (data && typeof data === 'object' && data.ok === false && data.type === 'PAYWALL') {
    return {
      ok: false,
      type: 'PAYWALL',
      code: data.code || 'FREE_LIMIT_REACHED',
      feature: 'coach',
      message: data.message || 'Limite atteinte. Passe Premium pour continuer.',
    };
  }

  // ✅ NOUVEAU: RATE_LIMIT response (Premium daily limit)
  if (data && typeof data === 'object' && data.ok === false && data.type === 'RATE_LIMIT') {
    return {
      ok: false,
      type: 'RATE_LIMIT',
      code: data.code || 'PREMIUM_DAILY_LIMIT_REACHED',
      feature: 'coach',
      message: data.message || 'Tu as atteint ta limite de messages pour aujourd\'hui. Reviens demain !',
    };
  }

  // Technical error
  if (error) {
    console.error('Coach chat error:', error);
    return {
      ok: false,
      type: 'ERROR',
      error: error.message || 'Erreur lors de la communication avec le coach',
    };
  }

  // Success
  if (!data) {
    return { ok: false, type: 'ERROR', error: 'Réponse vide du coach' };
  }

  return {
    ok: true,
    answer: data.answer || '',
    conversation_id: data.conversation_id || '',
    diploma: data.diploma || '',
    subject_code: data.subject_code || '',
    subject_label: data.subject_label || '',
  };
}
